import {
  ArrowBackRounded,
  ArrowForwardRounded,
  SkipNext,
} from "@mui/icons-material";
import { Autocomplete, Box, Grid, IconButton, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

interface HeatOption {
  Name: string;
  Id?: number;
  TreatmentNumber?: number | null;
  TreatmentCount?: number;
}

interface ProcessNavigatorProcess {
  ProcessId?: number;
  HeatName?: string;
  TreatmentNumber?: number | null;
  PrevProcessId?: number | null;
  NextProcessId?: number | null;
}

interface ApiResponse {
  ok: boolean;
  data?: { tables: { rows: HeatOption[] }[] };
  message?: string;
}

export interface ProcessNavigatorProps {
  process: ProcessNavigatorProcess | null;
  loadData: (processId: number | null) => Promise<void>;
  getLatestProcesses: () => Promise<ApiResponse>;
  searchProcesses: (term: string) => Promise<ApiResponse>;
  pause: () => void;
  resume: () => void;
  onError: (message: string) => void;
  initialProcessId?: number | null;
}

const extractRows = (resp: ApiResponse): HeatOption[] =>
  resp.data?.tables?.[0]?.rows ?? [];

const SEARCH_DEBOUNCE_MS = 1500;
const SEARCH_MIN_LENGTH = 2;
const INPUT_CHANGE_REASON = "input";

const getHeatName = (option: HeatOption): string => option.Name || "Unlinked";

const getPopupOptionLabel = (option: HeatOption): string => {
  if ((option.TreatmentCount ?? 0) > 1 && option.TreatmentNumber != null) {
    return `${option.Name} - ${option.TreatmentNumber}`;
  }

  return getHeatName(option);
};

const getHeatOptionKey = (option: HeatOption): string | number =>
  option.Id ?? getPopupOptionLabel(option);

export const ProcessNavigator = ({
  process,
  loadData,
  getLatestProcesses,
  searchProcesses,
  pause,
  resume,
  onError,
  initialProcessId,
}: ProcessNavigatorProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [selectedHeat, setSelectedHeat] = useState<HeatOption | null>(null);
  const [isUserInput, setIsUserInput] = useState(false);
  const [isFocused, setFocused] = useState(false);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedTerm(searchTerm),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const isSearching = isUserInput && debouncedTerm.length >= SEARCH_MIN_LENGTH;

  const { data: heats = [], isFetching: loading } = useQuery<HeatOption[]>({
    queryKey: isSearching
      ? ["process-navigator", "search", debouncedTerm]
      : ["process-navigator", "latest"],
    queryFn: async () => {
      try {
        const resp = isSearching
          ? await searchProcesses(debouncedTerm)
          : await getLatestProcesses();
        if (!resp.ok) {
          onError(resp.message ?? "Failed to load processes");
          return [];
        }
        return extractRows(resp);
      } catch {
        onError("An error occurred while loading processes");
        return [];
      }
    },
    enabled: isFocused,
    keepPreviousData: true,
  });

  const selectedValue =
    selectedHeat ??
    heats.find((option) => option.Id === process?.ProcessId) ??
    ({
      Name: process?.HeatName ?? "",
      Id: process?.ProcessId,
      TreatmentNumber: process?.TreatmentNumber ?? null,
      TreatmentCount: 0,
    } satisfies HeatOption);

  const handleFocus = () => {
    setFocused(true);
    pause();
  };

  const handleSelectionChange = async (
    _event: unknown,
    newValue: HeatOption | null,
  ) => {
    if (newValue) {
      setSelectedHeat(newValue);
      setIsUserInput(false);
      if (newValue.Id !== process?.ProcessId) {
        if (newValue.Id) {
          await loadData(newValue.Id);
        }
        setTimeout(() => {
          document.getElementById("dummyDiv")?.focus();
        }, 500);
      }
    } else {
      setSelectedHeat(null);
    }
  };

  const handleNavigate = async (processId: number | null) => {
    setSelectedHeat(null);
    setIsUserInput(false);
    await loadData(processId);
  };

  useEffect(() => {
    loadData(initialProcessId ?? null);
  }, []);

  return (
    <Box padding={1}>
      <Grid
        container
        direction="row"
        justifyContent="space-around"
        alignItems="center"
        spacing={1}
        sx={{
          display: "grid",
          gridTemplateColumns: "12% 64% 12% 12%",
        }}
      >
        <Grid item>
          <IconButton
            size="small"
            aria-label="previous"
            color="primary"
            disabled={!process?.PrevProcessId}
            onClick={() => handleNavigate(process?.PrevProcessId!)}
          >
            <ArrowBackRounded style={{ cursor: "pointer" }} />
          </IconButton>
        </Grid>
        <Grid item style={{ flexGrow: 1 }}>
          <Autocomplete
            autoHighlight
            blurOnSelect
            clearOnEscape
            selectOnFocus
            disableClearable
            popupIcon={null}
            options={heats}
            getOptionLabel={getHeatName}
            getOptionKey={getHeatOptionKey}
            renderOption={({ key, ...optionProps }, option) => (
              <li key={key} {...optionProps} style={{ whiteSpace: "nowrap" }}>
                {getPopupOptionLabel(option)}
              </li>
            )}
            isOptionEqualToValue={(option: HeatOption, value: HeatOption) =>
              option.Id === value.Id
            }
            inputValue={searchTerm}
            onFocus={handleFocus}
            onInputChange={(_event, newInputValue, reason) => {
              setSearchTerm(newInputValue);
              const isInputChange = reason === INPUT_CHANGE_REASON;
              setIsUserInput(isInputChange);
              if (isInputChange && !selectedHeat) {
                setSelectedHeat(selectedValue);
              }
            }}
            value={selectedValue}
            onChange={handleSelectionChange}
            loading={loading}
            onBlur={() => {
              setFocused(false);
              resume();
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Heat Name"
                variant="outlined"
                size="small"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    style: {
                      display: "flex",
                      justifyContent: "center",
                      textAlign: "center",
                      paddingRight: "8px",
                    },
                  },
                  htmlInput: {
                    ...params.inputProps,
                    style: {
                      textAlign: "center",
                    },
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid item>
          <IconButton
            aria-label="next"
            color="primary"
            disabled={!process?.NextProcessId}
            onClick={() => handleNavigate(process?.NextProcessId!)}
            size="small"
          >
            <ArrowForwardRounded style={{ cursor: "pointer" }} />
          </IconButton>
        </Grid>
        <Grid item>
          <IconButton
            aria-label="last"
            color="primary"
            disabled={!process?.NextProcessId}
            onClick={() => handleNavigate(null)}
            size="small"
            title="Last process"
            sx={
              process?.NextProcessId
                ? {
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: -4,
                      left: -4,
                      right: -4,
                      bottom: -4,
                      borderRadius: "50%",
                      border: (theme) =>
                        `2px solid ${theme.palette.secondary.main}`,
                      animation: "pulse 1.5s infinite",
                    },
                    "@keyframes pulse": {
                      "0%": { transform: "scale(0.95)", opacity: 0.7 },
                      "50%": { transform: "scale(1.05)", opacity: 1 },
                      "100%": { transform: "scale(0.95)", opacity: 0.7 },
                    },
                  }
                : undefined
            }
          >
            <SkipNext style={{ cursor: "pointer" }} />
          </IconButton>
        </Grid>
      </Grid>
    </Box>
  );
};
